/** Shared leaderboard contract and bounded, deterministic validation of submitted input tapes. */
import { courseRevision, getCourseData, timedCourseNames } from "@lache/courses";
import { PAD_SCHEMES } from "@lache/input/schemes";
import {
  type CourseData,
  createInputFrame,
  defaultTuning,
  Ev,
  HZ,
  Sim,
  TAPE_VERSION,
  type Tape,
  type TapeFrame,
  TapeReader,
  type Tuning,
  tuningHash,
  validateCourse,
} from "@lache/sim";
import { cleanPlayerName, EXPLICIT_NAME_ERROR, isExplicitName } from "./names";

export const MAX_SUBMISSION_BYTES = 1024 * 1024;
export const MAX_TAPE_TICKS = 5 * 60 * HZ;
export const SUBMISSION_INTERVAL_MS = 3000;
export const LEADERBOARD_TUNING = tuningHash(defaultTuning);
// Existing courses retain their boards through compatible additions. Gauntlet's revised layout
// gets a new key and accepts only the new ladder rule; old recordings keep their exact physics.
const preFlowTuning: Tuning = { ...defaultTuning, hurdleFlow: 0 };
const preLadderTuning: Tuning = { ...preFlowTuning, ladderPerfectDuck: 0 };
const legacyTuning: Tuning = { ...preLadderTuning, slidePerfectBoost: 0 };
export const LEADERBOARD_KEY_TUNING = tuningHash(legacyTuning);
const MAX_DATE_MS = 8_640_000_000_000_000;
const MAX_ACHIEVEMENT_CLOCK_SKEW_MS = 5 * 60 * 1000;
// Schemes only map physical controls into tape inputs; all use the same simulation physics.
const acceptedTuning = new Map<string, { tuning: Tuning; hash: string }>();
for (const tuning of [legacyTuning, preLadderTuning, preFlowTuning, defaultTuning]) {
  const family = { tuning, hash: tuningHash(tuning) };
  for (let padScheme = 0; padScheme < PAD_SCHEMES.length; padScheme++)
    acceptedTuning.set(tuningHash({ ...tuning, padScheme }), family);
}
export const LEADERBOARD_SIZE = 10;

export interface GlobalScore {
  name: string;
  time: number;
  falls: number;
  /** Server receipt time; authoritative for ranking equal times. */
  date: number;
  /** Client-reported finish time for display only; never used to rank or verify a run. */
  achievedAt?: number;
  tuning: string;
  /** Missing checkpoints are represented by zero (all real splits are positive). */
  splits: number[];
}

/** Keep historical scores intact while applying the current display-name policy to public reads. */
export function publicScore(entry: GlobalScore): GlobalScore {
  return { ...entry, name: cleanPlayerName(entry.name) };
}

export interface Submission {
  playerToken: string;
  name: string;
  tape: Tape;
  /** Absent on legacy clients, which recorded revision 1. */
  courseRevision?: number;
  /** Optional client-clock display metadata; simulation still verifies time and falls. */
  achievedAt?: number;
}

export interface BoardResult {
  course: string;
  tuning: string;
  entries: GlobalScore[];
}

export interface AcceptedScore {
  accepted: true;
  /** This identity's best saved score, which may precede the submitted run. */
  entry: GlobalScore;
  entries: GlobalScore[];
}

export interface RejectedScore {
  error: string;
  status: number;
  retryAfter?: number;
}

export type SubmissionResult = AcceptedScore | RejectedScore;

export class ScoreError extends Error {
  constructor(
    message: string,
    readonly status = 400,
  ) {
    super(message);
  }
}

function object(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function validAchievementTime(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0 && value <= MAX_DATE_MS;
}

/** Timed built-in courses only; nothing supplied by a client becomes world geometry. */
export function leaderboardCourse(name: string): CourseData {
  if (!timedCourseNames.includes(name)) throw new ScoreError("unknown timed course", 404);
  const course = getCourseData(name);
  validateCourse(course);
  return course;
}

/** Layout revisions get a new board; compatible mechanics retain other courses' existing times. */
export function leaderboardKey(course: CourseData): string {
  const text = JSON.stringify(course);
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return `${course.name}@${hash.toString(16).padStart(8, "0")}:${LEADERBOARD_KEY_TUNING}`;
}

/** Build a fresh tape, retaining bounded display metadata but discarding claimed scores and extra data. */
export function validateSubmission(value: unknown, course: string): Submission {
  if (!object(value)) throw new ScoreError("expected a score submission");
  if (
    typeof value.playerToken !== "string" ||
    !/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value.playerToken)
  )
    throw new ScoreError("invalid player token");
  if (typeof value.name !== "string" || value.name.length > 256) throw new ScoreError("invalid player name");
  if (isExplicitName(value.name)) throw new ScoreError(EXPLICIT_NAME_ERROR);
  const name = cleanPlayerName(value.name);
  const raw = value.tape;
  if (!object(raw)) throw new ScoreError("missing input tape");
  if (raw.version !== TAPE_VERSION || raw.course !== course)
    throw new ScoreError("wrong tape version or course");
  const revision = courseRevision(course);
  if ((value.courseRevision === undefined ? 1 : value.courseRevision) !== revision)
    throw new ScoreError("this course has changed; reload and run again", 409);
  const family = typeof raw.tuningHash === "string" ? acceptedTuning.get(raw.tuningHash) : undefined;
  if (!family) throw new ScoreError("this run uses different tuning; reload and run again", 409);
  if (course === "gauntlet" && family.tuning.ladderPerfectDuck < 1)
    throw new ScoreError("this course needs the current ladder rules; reload and run again", 409);
  if (
    typeof raw.seed !== "number" ||
    !Number.isInteger(raw.seed) ||
    raw.seed < 0 ||
    raw.seed > 0xffffffff ||
    (raw.respawn !== undefined && raw.respawn !== 0)
  )
    throw new ScoreError("the tape must start at the course start");
  if (!Array.isArray(raw.frames) || raw.frames.length === 0 || raw.frames.length > MAX_TAPE_TICKS)
    throw new ScoreError("invalid tape frames");
  let ticks = 0;
  const frames: TapeFrame[] = [];
  for (const frame of raw.frames) {
    if (!Array.isArray(frame) || frame.length !== 8 || !frame.every(Number.isInteger))
      throw new ScoreError("tape frames must contain eight integers");
    const f = frame as TapeFrame;
    if (
      f[0] < 1 ||
      f[0] > MAX_TAPE_TICKS ||
      f.slice(1, 5).some((axis) => axis < -127 || axis > 127) ||
      f[5] < 0 ||
      f[5] > 255 ||
      f[6] < 0 ||
      f[6] > 255 ||
      f[7] < 0 ||
      f[7] > 15
    )
      throw new ScoreError("tape input is outside the controller range");
    ticks += f[0];
    if (ticks > MAX_TAPE_TICKS) throw new ScoreError("tape exceeds five minutes");
    frames.push([...f]);
  }
  return {
    playerToken: value.playerToken.toLowerCase(),
    name,
    tape: { version: TAPE_VERSION, course, tuningHash: family.hash, seed: raw.seed, frames },
    courseRevision: revision,
    ...(validAchievementTime(value.achievedAt) ? { achievedAt: value.achievedAt } : {}),
  };
}

/** Replay in the Durable Object's CPU budget; time, falls, and splits come only from simulation events. */
export function replayScore(course: CourseData, submission: Submission, date: number): GlobalScore {
  // Clock mistakes must not reject a valid run. Missing or implausible display dates fall back to
  // the server receipt date; a slightly fast client clock is clamped to that same receipt time.
  const achievedAt =
    validAchievementTime(submission.achievedAt) &&
    submission.achievedAt <= date + MAX_ACHIEVEMENT_CLOCK_SKEW_MS
      ? Math.min(submission.achievedAt, date)
      : undefined;
  const family = acceptedTuning.get(submission.tape.tuningHash);
  if (!family) throw new ScoreError("this run uses different tuning; reload and run again", 409);
  if (course.name === "gauntlet" && family.tuning.ladderPerfectDuck < 1)
    throw new ScoreError("this course needs the current ladder rules; reload and run again", 409);
  const sim = new Sim(course, family.tuning);
  const reader = new TapeReader(submission.tape);
  const input = createInputFrame();
  let splits: number[] = [];
  let finished: GlobalScore | null = null;
  while (reader.next(input)) {
    sim.step(input);
    const events = sim.events;
    for (let i = 0; i < events.count; i++) {
      const type = events.type[i];
      if (type === Ev.RunStarted || type === Ev.RunCancelled) splits = [];
      else if (type === Ev.Checkpoint) {
        const index = events.a[i]!;
        while (splits.length <= index) splits.push(0);
        splits[index] = events.b[i]!;
      } else if (type === Ev.RunFinished) {
        // A touch/controller restart can begin another race while opponents are still playing.
        // The recorder retains that verified history; its snapshot ends at the latest finish.
        finished = {
          name: submission.name,
          time: events.a[i]!,
          falls: events.b[i]!,
          date,
          ...(achievedAt === undefined ? {} : { achievedAt }),
          tuning: family.hash,
          splits,
        };
      }
    }
  }
  if (finished) return finished;
  throw new ScoreError("the submitted run did not finish");
}

/** Read a bounded body even when Content-Length is absent or dishonest. */
export async function readSubmission(request: Request): Promise<unknown> {
  const declared = Number(request.headers.get("Content-Length"));
  if (declared > MAX_SUBMISSION_BYTES) throw new ScoreError("score submission is too large", 413);
  if (!request.body) throw new ScoreError("missing score submission");
  const reader = request.body.getReader();
  const chunks: Uint8Array[] = [];
  let size = 0;
  for (;;) {
    const part = await reader.read();
    if (part.done) break;
    size += part.value.byteLength;
    if (size > MAX_SUBMISSION_BYTES) {
      await reader.cancel();
      throw new ScoreError("score submission is too large", 413);
    }
    chunks.push(part.value);
  }
  const bytes = new Uint8Array(size);
  let offset = 0;
  for (const chunk of chunks) {
    bytes.set(chunk, offset);
    offset += chunk.byteLength;
  }
  try {
    return JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    throw new ScoreError("invalid score JSON");
  }
}

export async function sha256(text: string): Promise<string> {
  const bytes = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
  return Array.from(new Uint8Array(bytes), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

/** Re-run tapes accepted by the old first-finish verifier, without changing any stored board. */
export function scoreTapeHash(tape: Tape): Promise<string> {
  return sha256(`score-v2:${JSON.stringify(tape)}`);
}
